import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, differenceInDays } from 'date-fns';
import { CalendarIcon, ArrowRight, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { TimePicker } from '@/components/ui/time-picker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { getDateLocale } from '@/utils/dateLocale';

interface DateRangeStepProps {
  startDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  endDate: Date | null;
  onEndDateChange: (date: Date | null) => void;
  startTime: string;
  onStartTimeChange: (time: string) => void;
  endTime: string;
  onEndTimeChange: (time: string) => void;
  reason?: string;
  onReasonChange?: (reason: string) => void;
}

export function DateRangeStep({
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  startTime,
  onStartTimeChange,
  endTime,
  onEndTimeChange,
  reason = '',
  onReasonChange,
}: DateRangeStepProps) {
  const { t } = useTranslation('staff');
  const [startCalendarOpen, setStartCalendarOpen] = useState(false);
  const [endCalendarOpen, setEndCalendarOpen] = useState(false);

  const daysCount = startDate && endDate ? differenceInDays(endDate, startDate) + 1 : null;
  const timeError = startTime >= endTime;

  return (
    <div className="p-6 space-y-6">
      {/* Date selection */}
      <div className="space-y-4">
        <Label variant="section">
          {t('availability.wizard.steps.dates', 'Select Date Range')}
        </Label>

        {/* Start Date */}
        <div className="space-y-2">
          <Label>
            {t('availability.dialog.startDate', 'Start Date')}
          </Label>
          <Popover open={startCalendarOpen} onOpenChange={setStartCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full min-h-[56px] justify-start text-left font-normal text-base',
                  !startDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-3 h-5 w-5" />
                {startDate
                  ? format(startDate, 'EEEE, MMM d, yyyy', { locale: getDateLocale() })
                  : t('availability.dialog.pickDate', 'Pick a date')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate || undefined}
                onSelect={(date) => {
                  onStartDateChange(date || null);
                  setStartCalendarOpen(false);
                  // If end date is before new start date, clear it
                  if (date && endDate && date > endDate) {
                    onEndDateChange(null);
                  }
                }}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
                locale={getDateLocale()}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Arrow indicator */}
        <div className="flex justify-center">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            <ArrowRight className="w-5 h-5 text-gray-400 rotate-90" />
          </div>
        </div>

        {/* End Date */}
        <div className="space-y-2">
          <Label>
            {t('availability.dialog.endDate', 'End Date')}
          </Label>
          <Popover open={endCalendarOpen} onOpenChange={setEndCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full min-h-[56px] justify-start text-left font-normal text-base',
                  !endDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-3 h-5 w-5" />
                {endDate
                  ? format(endDate, 'EEEE, MMM d, yyyy', { locale: getDateLocale() })
                  : t('availability.dialog.pickEndDate', 'Pick an end date')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate || undefined}
                onSelect={(date) => {
                  onEndDateChange(date || null);
                  setEndCalendarOpen(false);
                }}
                disabled={(date) =>
                  date < new Date(new Date().setHours(0, 0, 0, 0)) ||
                  (startDate ? date < startDate : false)
                }
                initialFocus
                locale={getDateLocale()}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Time selection */}
      <div className="space-y-3">
        <Label variant="section">
          {t('availability.wizard.steps.time', 'Time')}
        </Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>
              {t('availability.dialog.startTime', 'Start Time')}
            </Label>
            <TimePicker
              value={startTime}
              onChange={onStartTimeChange}
              placeholder={t('availability.dialog.selectStartTime', 'Select start time')}
            />
          </div>
          <div className="space-y-2">
            <Label>
              {t('availability.dialog.endTime', 'End Time')}
            </Label>
            <TimePicker
              value={endTime}
              onChange={onEndTimeChange}
              placeholder={t('availability.dialog.selectEndTime', 'Select end time')}
            />
          </div>
        </div>
        {timeError && (
          <p className="text-sm text-red-600">
            {t('availability.errors.endTimeAfterStart', 'End time must be after start time')}
          </p>
        )}
      </div>

      {/* Optional reason */}
      {onReasonChange && (
        <div className="space-y-3">
          <Label variant="section">
            {t('availability.dialog.reason', 'Reason (optional)')}
          </Label>
          <Input
            placeholder={t('availability.dialog.reasonPlaceholder.dateRange', 'e.g., Holiday availability, Special event')}
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            className="min-h-[48px]"
          />
        </div>
      )}

      {/* Days count info */}
      {daysCount !== null && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                {t('availability.wizard.dateRange.daysSelected', '{{count}} days selected', {
                  count: daysCount,
                })}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                {startDate && endDate && (
                  <>
                    {format(startDate, 'MMM d', { locale: getDateLocale() })} -{' '}
                    {format(endDate, 'MMM d, yyyy', { locale: getDateLocale() })}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
